/** Drag-and-drop placements of curated models on the room image (per room). */

export const CURATED_MODEL_DRAG_MIME = 'application/x-dx-curated-model';

function storageKey(roomId) {
  return `photorealistic:placed-curated:${String(roomId)}`;
}

export function normalizePlacedCuratedModels(items) {
  if (!Array.isArray(items)) return [];
  return items
    .map((item) => {
      const placementId = item?.placementId;
      const roomObjectId = item?.roomObjectId ?? item?.id;
      const x = Number(item?.x);
      const y = Number(item?.y);
      const width = Number(item?.width);
      const aspectRatio = Number(item?.aspectRatio);
      if (placementId == null || roomObjectId == null) return null;
      if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(width)) return null;
      return {
        placementId: String(placementId),
        roomObjectId,
        name: item?.name ?? 'Model',
        category: item?.category ?? 'Other',
        x,
        y,
        width: Math.max(24, width),
        aspectRatio: Number.isFinite(aspectRatio) && aspectRatio > 0 ? aspectRatio : 1,
      };
    })
    .filter(Boolean);
}

export function loadPlacedCuratedModelsForRoom(roomId) {
  if (roomId == null) return [];
  try {
    const raw = localStorage.getItem(storageKey(roomId));
    if (!raw) return [];
    const parsed = normalizePlacedCuratedModels(JSON.parse(raw));
    return parsed.map((p) => ({ ...p, thumbSrc: '' }));
  } catch {
    return [];
  }
}

export function savePlacedCuratedModelsForRoom(roomId, items) {
  if (roomId == null) return;
  try {
    const normalized = normalizePlacedCuratedModels(items);
    if (normalized.length === 0) {
      localStorage.removeItem(storageKey(roomId));
    } else {
      localStorage.setItem(storageKey(roomId), JSON.stringify(normalized));
    }
  } catch (err) {
    console.error('Failed to save placed curated models for room', roomId, err);
  }
}

/** Natural width / height of an image URL (for placement aspect ratio). */
export function loadImageAspectRatio(src) {
  return new Promise((resolve) => {
    const url = typeof src === 'string' ? src.trim() : '';
    if (!url) {
      resolve(1);
      return;
    }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const w = Number(img.naturalWidth || img.width || 0);
      const h = Number(img.naturalHeight || img.height || 0);
      resolve(w > 0 && h > 0 ? w / h : 1);
    };
    img.onerror = () => resolve(1);
    img.src = url;
  });
}

/**
 * Sized drag ghost matching on-canvas placement (previewWidthPx ≈ screen placement width).
 * Pass a loaded <img> when possible. Returns cleanup — call on dragend.
 */
export function attachCuratedModelDragImage(dataTransfer, thumbSrcOrImg, previewWidthPx) {
  if (!dataTransfer) return () => {};

  const maxW = Math.max(32, Number(previewWidthPx) || 72);
  let ghostEl = null;

  const cleanup = () => {
    if (ghostEl?.parentNode) {
      ghostEl.parentNode.removeChild(ghostEl);
    }
    ghostEl = null;
  };

  const applyFromImage = (sourceImage) => {
    const nw = Number(sourceImage?.naturalWidth) || Number(sourceImage?.width) || 1;
    const nh = Number(sourceImage?.naturalHeight) || Number(sourceImage?.height) || 1;
    const ar = nw / nh;
    let w = maxW;
    let h = maxW / ar;
    if (h > maxW) {
      h = maxW;
      w = h * ar;
    }

    const wrap = document.createElement('div');
    wrap.style.cssText = [
      'position:fixed',
      'left:-9999px',
      'top:0',
      `width:${w}px`,
      `height:${h}px`,
      'opacity:0.88',
      'pointer-events:none',
      'border-radius:6px',
      'overflow:hidden',
      'background:#fff',
      'box-shadow:0 2px 10px rgba(0,0,0,0.18)',
    ].join(';');

    const imgEl =
      sourceImage instanceof HTMLImageElement
        ? sourceImage.cloneNode(true)
        : document.createElement('img');
    if (!(sourceImage instanceof HTMLImageElement)) {
      imgEl.src = sourceImage;
    }
    imgEl.alt = '';
    imgEl.draggable = false;
    imgEl.style.cssText = 'width:100%;height:100%;object-fit:contain;display:block;';
    wrap.appendChild(imgEl);
    document.body.appendChild(wrap);
    ghostEl = wrap;

    try {
      dataTransfer.setDragImage(wrap, w / 2, h / 2);
    } catch {
      cleanup();
    }
  };

  if (thumbSrcOrImg instanceof HTMLImageElement) {
    if (thumbSrcOrImg.complete && thumbSrcOrImg.naturalWidth > 0) {
      applyFromImage(thumbSrcOrImg);
      return cleanup;
    }
    const src = thumbSrcOrImg.currentSrc || thumbSrcOrImg.src;
    if (!src) return cleanup;
    thumbSrcOrImg = src;
  }

  if (typeof thumbSrcOrImg !== 'string' || !thumbSrcOrImg.trim()) {
    return cleanup;
  }

  const img = new Image();
  img.crossOrigin = 'anonymous';
  const onReady = () => applyFromImage(img);
  img.onload = onReady;
  img.onerror = cleanup;
  img.src = thumbSrcOrImg;
  if (img.complete && img.naturalWidth > 0) {
    onReady();
  }

  return cleanup;
}

export function parseCuratedModelDragPayload(dataTransfer) {
  if (!dataTransfer) return null;
  const raw =
    dataTransfer.getData(CURATED_MODEL_DRAG_MIME) ||
    dataTransfer.getData('application/json') ||
    dataTransfer.getData('text/plain');
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    const id = parsed?.id ?? parsed?.roomObjectId;
    if (id == null) return null;
    return {
      id,
      name: parsed?.name ?? 'Model',
      category: parsed?.category ?? 'Other',
      thumbSrc: typeof parsed?.thumbSrc === 'string' ? parsed.thumbSrc : '',
    };
  } catch {
    return null;
  }
}
